<script setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import SvgIcon from "@/shared/ui/svg-icon/SvgIcon.vue";

/**
 * Compact trigger button with linear progress indicator.
 * Used in page headers next to primary action (e.g. Save) to toggle <Stepper />.
 *
 * Example:
 *   <StepperTrigger
 *     v-model:open="open"
 *     :done-count="3"
 *     :total-steps="6"
 *     :progress="50"
 *   />
 */
const props = defineProps({
  open: { type: Boolean, default: false },
  doneCount: { type: Number, default: 0 },
  totalSteps: { type: Number, default: 0 },
  progress: { type: Number, default: 0 },
  label: { type: String, default: "" },
  icon: { type: String, default: "sli-list" },
  complete: { type: Boolean, default: null },
});

const emit = defineEmits(["update:open"]);

const { t } = useI18n();

const resolvedLabel = computed(() => props.label || t("Прогресс"));

const isComplete = computed(() => {
  if (typeof props.complete === "boolean") return props.complete;
  return props.totalSteps > 0 && props.doneCount >= props.totalSteps;
});

const RING_SIZE = 40;
const RING_CENTER = RING_SIZE / 2;
const RING_RADIUS = 15.5;
const RING_CIRC = 2 * Math.PI * RING_RADIUS;

const ringProgress = computed(() => {
  const total = props.totalSteps || 0;
  if (total <= 0) return 0;
  return Math.min(Math.max(props.doneCount / total, 0), 1);
});

const ringDash = computed(() => {
  const filled = RING_CIRC * ringProgress.value;
  return `${filled} ${RING_CIRC - filled}`;
});

function onToggle() {
  emit("update:open", !props.open);
}
</script>

<template>
  <button
    type="button"
    class="stepper-trigger"
    :class="{
      'stepper-trigger--open': open,
      'stepper-trigger--complete': isComplete,
    }"
    :aria-expanded="open ? 'true' : 'false'"
    @click="onToggle"
  >
    <SvgIcon :name="icon" class="stepper-trigger__icon" style="fill: none" />
    <div class="stepper-trigger__body">
      <div class="stepper-trigger__meta">
        <div class="stepper-trigger__label">{{ resolvedLabel }}</div>
        <div class="stepper-trigger__counter">
          {{ doneCount }} / {{ totalSteps }}
        </div>
      </div>
      <div class="stepper-trigger__bar">
        <div class="stepper-trigger__fill" :style="{ width: progress + '%' }" />
      </div>
    </div>
    <SvgIcon
      :name="open ? 'sli-shevron-left' : 'huge-chevron-right'"
      class="stepper-trigger__chevron"
    />

    <div class="stepper-trigger__ring" aria-hidden="true">
      <svg
        :viewBox="`0 0 ${RING_SIZE} ${RING_SIZE}`"
        class="stepper-trigger__ring-svg"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle
          class="stepper-trigger__ring-track"
          :cx="RING_CENTER"
          :cy="RING_CENTER"
          :r="RING_RADIUS"
          fill="none"
        />
        <circle
          class="stepper-trigger__ring-progress"
          :cx="RING_CENTER"
          :cy="RING_CENTER"
          :r="RING_RADIUS"
          fill="none"
          :stroke-dasharray="ringDash"
          :transform="`rotate(-90 ${RING_CENTER} ${RING_CENTER})`"
        />
      </svg>
      <div class="stepper-trigger__ring-counter">
        {{ doneCount }}/{{ totalSteps }}
      </div>
    </div>
  </button>
</template>

<style scoped>
.stepper-trigger {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 36px;
  padding: 4px 12px;
  border: 1px solid hsl(var(--border));
  border-radius: var(--radius);
  background: hsl(var(--card));
  cursor: pointer;
  transition: all 180ms ease;
  min-width: 200px;
  max-width: 300px;
  color: hsl(var(--foreground));
  font: inherit;
}

.stepper-trigger:hover {
  border-color: rgba(99, 102, 241, 0.5);
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.08);
}

.stepper-trigger:focus-visible {
  outline: 2px solid #6366f1;
  outline-offset: 2px;
}

.stepper-trigger--open {
  border-color: rgba(99, 102, 241, 0.6);
  background: rgba(99, 102, 241, 0.04);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.stepper-trigger--complete {
  border-color: rgba(16, 185, 129, 0.5);
}

.stepper-trigger__icon {
  width: 16px;
  height: 16px;
  color: hsl(var(--muted-foreground));
  flex-shrink: 0;
}

.stepper-trigger__body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.stepper-trigger__meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.stepper-trigger__label {
  font-size: 11px;
  font-weight: 500;
  color: hsl(var(--muted-foreground));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.stepper-trigger__counter {
  font-size: 12px;
  font-weight: 600;
  color: hsl(var(--foreground));
  flex-shrink: 0;
}

.stepper-trigger__bar {
  height: 4px;
  border-radius: 999px;
  background: rgba(99, 102, 241, 0.15);
  overflow: hidden;
}

.stepper-trigger__fill {
  height: 100%;
  background: linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%);
  border-radius: 999px;
  transition: width 400ms cubic-bezier(0.16, 1, 0.3, 1);
}

.stepper-trigger--complete .stepper-trigger__fill {
  background: linear-gradient(90deg, #10b981 0%, #34d399 100%);
}

.stepper-trigger__chevron {
  width: 14px;
  height: 14px;
  color: hsl(var(--muted-foreground));
  flex-shrink: 0;
  transition: color 200ms ease;
}

.stepper-trigger:hover .stepper-trigger__chevron,
.stepper-trigger--open .stepper-trigger__chevron {
  color: #6366f1;
}

.stepper-trigger__ring {
  display: none;
  position: relative;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 50%;
  background: transparent;
}

.stepper-trigger__ring-svg {
  position: relative;
  width: 100%;
  height: 100%;
  transition: transform 200ms ease;
}

.stepper-trigger__ring-track {
  stroke: hsl(var(--border));
  stroke-width: 4.5;
}

.stepper-trigger__ring-progress {
  stroke: #6366f1;
  stroke-width: 4.5;
  stroke-linecap: round;
  transition: stroke-dasharray 500ms cubic-bezier(0.16, 1, 0.3, 1);
}

.stepper-trigger--complete .stepper-trigger__ring-progress {
  stroke: #079455;
}

.stepper-trigger__ring-counter {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.2;
  color: hsl(var(--foreground));
}

@media (max-width: 1500px) {
  .stepper-trigger {
    min-width: 0;
    max-width: none;
    width: 40px;
    height: 40px;
    padding: 0;
    gap: 0;
    border: none;
    border-radius: 50%;
    background: transparent;
    box-shadow: none;
  }

  .stepper-trigger:hover {
    border-color: transparent;
    box-shadow: none;
  }

  .stepper-trigger:hover .stepper-trigger__ring-svg {
    transform: scale(1.05);
  }

  .stepper-trigger--open {
    background: transparent;
    box-shadow: none;
  }

  .stepper-trigger__icon,
  .stepper-trigger__body,
  .stepper-trigger__chevron {
    display: none;
  }

  .stepper-trigger__ring {
    display: block;
  }
}

@media (prefers-reduced-motion: reduce) {
  .stepper-trigger,
  .stepper-trigger__fill,
  .stepper-trigger__chevron,
  .stepper-trigger__ring-svg,
  .stepper-trigger__ring-seg {
    transition: none;
  }
}
</style>
